/**
 * Attach a Microsoft sign-in to an existing email/password account when the addresses differ.
 * The password proves the local account belongs to this person.
 */
import { useState } from 'react';
import { apiPostNoAuth } from '@/api/client';
import { acquireMicrosoftAccessToken } from '@/auth/microsoftToken';
import { isBlockedEmailDomain, BLOCKED_EMAIL_MESSAGE } from '@/auth/blockedEmailDomains';
import Turnstile, { isCaptchaEnabled } from '@/components/Turnstile';

export default function LinkExistingAccount({ onLinked }: { onLinked: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password) {
      setError('Enter the email and password for your existing SILISENSE account.');
      return;
    }
    if (isBlockedEmailDomain(trimmed)) {
      setError(BLOCKED_EMAIL_MESSAGE);
      return;
    }
    if (isCaptchaEnabled() && !captchaToken) {
      setError('Please complete the CAPTCHA challenge.');
      return;
    }
    setLoading(true);
    try {
      const microsoftAccessToken = await acquireMicrosoftAccessToken();
      await apiPostNoAuth('/v1/auth/sso/link-password', {
        microsoftAccessToken,
        email: trimmed,
        password,
        captchaToken,
      });
      onLinked();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not link the accounts.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="login-form" aria-label="Link existing email account" style={{ textAlign: 'left' }}>
      <p className="login-hint">
        If you already have a SILISENSE email and password, enter them here. Microsoft sign-in will open that same account.
      </p>
      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}
      <div className="form-group">
        <label htmlFor="link-email">Existing account email</label>
        <input
          id="link-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="form-input"
          disabled={loading}
        />
      </div>
      <div className="form-group">
        <label htmlFor="link-password">Password</label>
        <input
          id="link-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="form-input"
          disabled={loading}
        />
      </div>
      <Turnstile onToken={setCaptchaToken} />
      <button type="submit" className="btn-primary btn-login" disabled={loading}>
        {loading ? 'Linking…' : 'Link my account'}
      </button>
    </form>
  );
}
